import '@beyondtrust/btux-iconography-web';
import { localized, msg } from '@lit/localize';
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { ProductMenuItem, SiteData } from '../../types';
import '../navigation-menu-content/navigation-menu-content';
import '../site-selector/site-selector';
import '../user-profile/user-profile';
import styles from './sidebar-menu-navigation-shell.styles.scss';

export const SIDEBAR_MENU_NAVIGATION_SHELL_TAG = 'platform-sidebar-menu-navigation-shell' as const;

/**
 * Sidebar-menu navigation shell — rendered by `platform-header` when a product sets
 * `navigation-layout="sidebar"`. Reuses the existing chrome around the API-driven menu.
 */
@customElement(SIDEBAR_MENU_NAVIGATION_SHELL_TAG)
@localized()
export class PlatformSidebarMenuNavigationShell extends LitElement {
  static override styles = [styles];

  @property({ attribute: false })
  productMenuItems: ProductMenuItem[] = [];

  @property({ attribute: false, type: Object })
  currentProduct!: ProductMenuItem;

  @property({ attribute: false })
  site!: SiteData;

  @property()
  activeRoute?: string;

  /** Deployment environment badge text (e.g. "Staging"/"Preview"); empty in production. */
  @property()
  environmentLabel = '';

  override render() {
    return html`
      <div class="platform-sidebar-shell">
        <header class="platform-sidebar-shell-bar">
          <div class="platform-sidebar-shell-brand">
            <a href="/" aria-label="${msg('Home')}" data-dd-action-name="Pathfinder Home">
              <btux-svg name="bt-logo"></btux-svg>
            </a>
            <platform-site-selector
              selected=${this.site.site_id}
              data-dd-action-name="site-selector"
            ></platform-site-selector>
            <div class="platform-sidebar-shell-divider" role="separator" aria-orientation="vertical"></div>
            <div class="platform-sidebar-shell-product">
              <slot name="breadcrumbs">${this.activeRoute || this.currentProduct.name}</slot>
              ${when(
                // Same marker the overlay's product-header-link shows in non-production
                // environments. Deliberately not localized: an internal-only label.
                this.environmentLabel,
                () => html`<span class="platform-sidebar-shell-environment">${this.environmentLabel}</span>`,
              )}
            </div>
          </div>
          <div class="platform-sidebar-shell-actions">
            <a
              href="${this.currentProduct.helpUrl}"
              aria-label="${msg('Help')}"
              target="_blank"
              rel="noopener noreferrer"
            >
              <btux-svg name="circle-question"></btux-svg>
            </a>
            <platform-user-profile data-dd-action-name="user-profile-popout"></platform-user-profile>
          </div>
        </header>

        <!-- Layout div, not <nav>: platform-menu already owns the navigation landmark. -->
        <!-- <div class="platform-sidebar-shell-menu">
          // Commented out for simplification of development.
          <platform-navigation-menu-content .productMenuItems=${this.productMenuItems}>
            <slot name="admin-menu" slot="admin-menu"></slot>
            <slot name="platform-menu" slot="platform-menu"></slot>
            <slot name="insights-menu" slot="insights-menu"></slot>
            <slot name="epml-menu" slot="epml-menu"></slot>
            <slot name="pmc-menu" slot="pmc-menu"></slot>
            <slot name="passwordsafe-menu" slot="passwordsafe-menu"></slot>
            <slot name="privileged-remote-access-menu" slot="privileged-remote-access-menu"></slot>
            <slot name="remote-support-menu" slot="remote-support-menu"></slot>
            <slot name="secrets-menu" slot="secrets-menu"></slot>
            <slot name="entitle-menu" slot="entitle-menu"></slot>
            <slot name="ai-coworkers-menu" slot="ai-coworkers-menu"></slot>
            <slot name="reporting-insights-menu" slot="reporting-insights-menu"></slot>
          </platform-navigation-menu-content>
        </div> -->
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SIDEBAR_MENU_NAVIGATION_SHELL_TAG]: PlatformSidebarMenuNavigationShell;
  }
}
